import {Injectable} from '@angular/core';
import {SpatialQueryService} from '../../design/services/spatial-query.service';
import {AppConfigService} from './app-config.service';
import {PlanModelService} from './plan-model.service';
import {FeaturesRepositoryStore} from './features-repository-store';
import {LayerDefinitionService} from '../../design/esri-nlr/layers/definitions/layer-definition-service';
import {reCheckAngleFormats} from '../../design/services/features/angle/angle-tools';
import {from, map, Observable, of, tap, throwError} from 'rxjs';
import {catchError, switchMap} from 'rxjs/operators';
import {equals} from './data-util';
import {EsriModuleService} from '../../design/esri-nlr/services/esri-module.service';
import {ArcGisAuthService} from '../../design/services/arc-gis-auth.service';
import {forkJoinDefaultIfEmpty} from '../observable.utils';
import {NotificationAlertsService} from '../../common/notification-alerts/notification-alerts.service';

/* eslint-disable @typescript-eslint/no-explicit-any */
@Injectable({
    providedIn: 'root'
})
export class FeaturesRepositoryService {
    title = 'Features Repository Service';
    private hasEditsFlag = false;
    featureLayerMap = {};
    featureLayerConstructor: any;

    constructor(
        private featuresRepositoryStore: FeaturesRepositoryStore,
        private spatialQueryService: SpatialQueryService,
        private appConfigService: AppConfigService,
        private planModelService: PlanModelService,
        private layerDefinitionService: LayerDefinitionService,
        private esriModuleService: EsriModuleService,
        private arcGisAuthService: ArcGisAuthService,
        private notificationAlertsService: NotificationAlertsService
    ) {}

    /**
     * Create blank repository
     */
    createRepository() {
        // TODO ??????? setupDb
        // return this.featuresRepositoryStore.setupDb();
    }

    /**
     * Clear all results from all layer object stores on spatialFeaturesRepositoryDb database
     */
    clearRepository() {
        // let deferred = $q.defer();
        //
        // deferred.resolve();
        // appPromises.push(deferred.promise);
        //
        // return deferred.promise;
        return of();
    }

    /**
     * Sanitise features (remove nulls, duplicates etc)
     * @param features
     * @returns sanitised features
     */
    sanitiseFeatures(featureLayer, features) {
        const fieldNames = this.layerDefinitionService.getFieldNames(this.getFeatureClass(featureLayer));

        const featureMap = features
            .filter(f => f)
            .reduce((acc, f) => {
                Object.keys(f.attributes)
                    .filter(k => !fieldNames.includes(k))
                    .forEach(k => delete f.attributes[k]);
                acc[f.attributes.id] = f;
                return acc;
            }, {});

        const featureIds = features.map(f => f.attributes.id).filter((v, i, a) => a.indexOf(v) === i);

        const featuresToKeep = featureIds.map(v => featureMap[v]);

        const planSpatialReference = this.planModelService.getSpatialReference();
        featuresToKeep
            .filter(f => f.geometry && f.geometry.spatialReference)
            .forEach(f => {
                if (planSpatialReference.wkid && f.geometry.spatialReference.wkid !== planSpatialReference.wkid) {
                    f.geometry.spatialReference.wkid = planSpatialReference.wkid;
                } else if (planSpatialReference.wkt && f.geometry.spatialReference.wkt !== planSpatialReference.wkt) {
                    f.geometry.spatialReference.wkt = planSpatialReference.wkt;
                }
            });

        const featuresToKeepObjectIds = featuresToKeep.filter(f => f.attributes.objectid).map(f => f.attributes.objectid);

        const featuresToDelete = features
            .filter(f => f.attributes.objectid)
            .map(f => f.attributes.objectid)
            .filter(objectId => !featuresToKeepObjectIds.includes(objectId))
            .map(objectId => ({attributes: {objectid: objectId}}));

        return {featuresToKeep: featuresToKeep, featuresToDelete: featuresToDelete};
    }

    addFeatures(featureClass: string, features: any): Observable<void> {
        const logPrefix = `${this.title} - addFeatures`;
        console.debug(`${logPrefix} - featureClass: ${featureClass}, features count: ${features.length}`);

        if (featureClass === 'angle') {
            reCheckAngleFormats(features);
        }

        const featureLayer = this.getFeatureLayer(featureClass);
        const {featuresToKeep, featuresToDelete} = this.sanitiseFeatures(featureLayer, features);

        // eslint-disable-next-line max-len
        const deleteObservable = featuresToDelete.length ? this.applyEdits(featureClass, null, null, featuresToDelete) : of(undefined);

        console.debug(
            `${logPrefix} - featureClass: ${featureClass}`,
            `featuresToDelete count: ${featuresToDelete.length}`,
            `featuresToKeep count: ${featuresToKeep.length}`
        );
        return deleteObservable.pipe(
            switchMap(() => this.featuresRepositoryStore.addFeatures(featureClass, featuresToKeep)),
            tap(() => {
                console.debug(`${logPrefix} - success`);
                this.hasEditsFlag = Object.keys(this.getEdits()).length > 0;
            }),
            catchError(reason => {
                console.debug(`${logPrefix} - error:`, reason);
                throw reason; // Re-throw error for further handling in observable chain
            })
        );
    }

    clearFeatures(featureClass: string) {
        console.debug(`${this.title} - clearFeatures - featureClass: ${featureClass}`);
        return this.featuresRepositoryStore.clearFeatures(featureClass);
    }

    clearAllFeatures() {
        console.debug(`${this.title} - clearAllFeatures`);
        this.featuresRepositoryStore.clearAllFeatures();
    }

    refresh(): Observable<any> {
        console.debug('refresh');

        const featureLayers = this.getFeatureLayers();
        const queries = featureLayers.map(featureLayer =>
            this.spatialQueryService.queryFeatures(featureLayer).pipe(map(features => this.addFeatures(this.getFeatureClass(featureLayer), features)))
        );

        return forkJoinDefaultIfEmpty(queries);
    }

    getFeatureCount(featureClasses) {
        featureClasses = featureClasses.map(v => v.toLowerCase());
        const allFeatures = this.featuresRepositoryStore.getFeatureClassMap();
        return (featureClasses || this.featuresRepositoryStore.getFeatureClasses())
            .filter(k => allFeatures[k])
            .reduce((acc, k) => {
                acc += allFeatures[k].length;
                return acc;
            }, 0);
    }

    geometryFeatureClasses = ['angle', 'annotation', 'controlpoint', 'constructline', 'constructpoint', 'point', 'polygon', 'line'];

    getGeometryFeatureClasses() {
        return this.geometryFeatureClasses;
    }

    getGeometryFeatureCount() {
        return this.getFeatureCount(this.geometryFeatureClasses);
    }

    getFeatures(featureClass) {
        featureClass = featureClass.toLowerCase();
        //console.debug(`${this.title} - getFeatures(${featureClass})`);

        return this.featuresRepositoryStore.getFeatureClassMap()[featureClass];
    }

    getFeatureClasses() {
        return this.featuresRepositoryStore.getFeatureClasses();
    }

    getAllFeatures() {
        return this.featuresRepositoryStore.getFeatureClasses().reduce((acc, k) => {
            acc.push(...this.getFeatures(k));
            return acc;
        }, []);
    }

    getFeatureClass(featureClassOrLayer) {
        //console.log(`${this.title}: getFeatureClass: ${featureClassOrLayer}`);
        let featureClass = (typeof featureClassOrLayer === 'string' ? featureClassOrLayer : featureClassOrLayer.id).toLowerCase();
        if (featureClass.endsWith('featurelayer')) {
            featureClass = featureClass.slice(0, featureClass.indexOf('featurelayer'));
        }

        return this.getFeatureClasses().includes(featureClass) ? featureClass : '';
    }

    partitionFeatureEditResults(featureEditResults) {
        return featureEditResults.reduce(
            (acc, featureEditResult) => {
                if (featureEditResult.success) {
                    acc.successes.push(featureEditResult);
                } else {
                    acc.errors.push(featureEditResult);
                }
                return acc;
            },
            {errors: [], successes: []}
        );
    }

    toGraphics(values: any[]) {
        return values.map((v: any) => {
            if (!v.toJson) {
                return new this.esriModuleService.Graphic(v);
            }

            if (v.geometry && !v.geometry.toJson) {
                v.geometry = new this.esriModuleService.Graphic({attributes: v.attributes, geometry: v.geometry}).geometry;
            }

            return v;
        });
    }

    getFeatureIds(features: any[]) {
        return features.map(v => v.attributes.id);
    }

    addedDeletedIds = {};

    clearAddedDeletedIds(featureClass) {
        if (this.addedDeletedIds[featureClass]) {
            this.addedDeletedIds[featureClass].length = 0;
        }
    }

    getAddedDeletedIds(featureClass) {
        if (!this.addedDeletedIds[featureClass]) {
            this.addedDeletedIds[featureClass] = [];
        }
        return this.addedDeletedIds[featureClass];
    }

    applyEdits(featureClass: string, adds: any[], updates: any[], deletes: any[], options: any = {}): Observable<any> {
        featureClass = featureClass.toLowerCase();
        console.debug(`applyEdits() - ${featureClass}`);

        const featureLayer = this.getFeatureLayer(featureClass);
        if (!featureLayer) {
            const err = `Feature layer not found for feature type: ${featureClass}`;
            console.error(`applyEdits() ${err}`);
            return throwError(err);
        }

        adds = adds || [];
        updates = updates || [];
        deletes = deletes || [];

        if (adds.length === 0 && updates.length === 0 && deletes.length === 0) {
            return of({
                adds: [],
                updates: [],
                deletes: []
            });
        }

        const deletesIds = deletes.filter(d => typeof d.attributes.id === 'number').map(d => d.attributes.id);

        const {featuresToKeep: addsToKeep, featuresToDelete: addsToDelete} = this.sanitiseFeatures(featureLayer, adds);

        const {featuresToKeep: updatesToKeep, featuresToDelete: updatesToDelete} = this.sanitiseFeatures(featureLayer, updates);

        const allDeletesObjectIds = [...deletes, ...addsToDelete, ...updatesToDelete]
            .map(v => v.attributes.objectid)
            .filter((v, i, a) => typeof v === 'number' && a.indexOf(v) === i);

        const updateIds = this.getFeatureIds(updatesToKeep);
        const filteredAddsToKeep = addsToKeep.filter(
            v => !updateIds.includes(v.attributes.id) && !(v.attributes.objectid && allDeletesObjectIds.includes(v.attributes.objectid))
        );

        let addsToKeepGraphics = this.toGraphics(filteredAddsToKeep);

        if ((featureClass === 'angle' || featureClass === 'annotation') && addsToKeepGraphics.some(f => !f.geometry)) {
            console.warn(`applyEdits - attempting to add ${featureClass} features without geometries.`);
            addsToKeepGraphics = addsToKeepGraphics.filter(f => f.geometry);
        }

        const fields = this.layerDefinitionService.getFields(featureClass);
        const nullableFieldNames = fields.filter(f => f.nullable && !f.name.startsWith('attribute')).map(field => field.name);

        addsToKeepGraphics.forEach(f => {
            nullableFieldNames.filter(name => !Object.prototype.hasOwnProperty.call(f.attributes, name)).forEach(name => (f.attributes[name] = null));
        });

        const filteredUpdatesToKeep = updatesToKeep.filter(v => !(v.attributes.objectid && allDeletesObjectIds.includes(v.attributes.objectid)));

        const updatesToKeepGraphics = this.toGraphics(filteredUpdatesToKeep);
        const allDeletes = allDeletesObjectIds.map(objectId => ({attributes: {objectid: objectId}}));

        if (addsToKeepGraphics.length === 0 && updatesToKeepGraphics.length === 0 && allDeletes.length === 0 && deletesIds.length === 0) {
            return of({
                adds: [],
                updates: [],
                deletes: []
            });
        }

        const applyEditsObservable = options.client
            ? of({
                  addResults: addsToKeep.map((v: any) => ({objectId: v.attributes.id, objectid: v.attributes.id, success: true})),
                  updateResults: updatesToKeep.map((v: any) => ({objectId: v.attributes.objectid, objectid: v.attributes.objectid, success: true})),
                  deleteResults: allDeletes.map(v => ({
                      objectId: v.attributes.objectid,
                      objectid: v.attributes.objectid,
                      success: true
                  }))
              })
            : from(
                  featureLayer
                      .applyEdits(addsToKeepGraphics, updatesToKeepGraphics, allDeletes)
                      .then((addResults: any, updateResults: any, deleteResults: any) => ({
                          addResults: addResults,
                          updateResults: updateResults,
                          deleteResults: deleteResults
                      }))
              );

        return applyEditsObservable.pipe(
            map((res: any) => {
                console.debug(`applyEdits() - ${featureClass} success`);

                this.hasEditsFlag = true;

                const adds = this.partitionFeatureEditResults(res.addResults);
                if (adds.errors.length) {
                    console.warn(`${this.title} - applyEdits - add errors:`, adds.errors);
                }

                const deletes = this.partitionFeatureEditResults(res.deleteResults);
                if (deletes.errors.length) {
                    console.warn(`${this.title} - applyEdits - delete errors:`, deletes.errors);
                }

                const updates = this.partitionFeatureEditResults(res.updateResults);
                if (updates.errors.length) {
                    const validErrors = updates.errors.filter(f => f.error.code !== 1011);
                    if (validErrors.length) {
                        console.warn(`${this.title} - applyEdits - update errors:`, validErrors);
                    }
                }

                const addsObjectIds = adds.successes.map((a: any) => a.objectId);
                const deletesObjectIds = deletes.successes.map((d: any) => d.objectId);
                const updatesObjectIds = updates.successes.map((u: any) => u.objectId);

                const allEditsObjectIds = [...addsObjectIds, ...deletesObjectIds, ...updatesObjectIds];

                if (allEditsObjectIds.length === 0 && deletesIds.length === 0) {
                    console.debug(`applyEdits - no successful edits`);
                    return {
                        adds: res.addResults,
                        updates: res.updateResults,
                        deletes: res.deleteResults
                    };
                }

                // Map successful adds and updates back to their feature instances

                const successfulAdds = res.addResults
                    .map((v: any, i: number) => ({feature: addsToKeepGraphics[i], result: v}))
                    .filter((v: any) => v.result.success)
                    .map((v: any) => {
                        v.feature.objectid = v.result.objectid;
                        return v.feature;
                    });

                const successfulUpdates = res.updateResults
                    .map((v: any, i: number) => ({feature: updatesToKeepGraphics[i], result: v}))
                    .filter((v: any) => v.result.success)
                    .map((v: any) => v.feature);

                const updatedFeatures = this.toGraphics([...successfulAdds, ...successfulUpdates].map(f => (f.toJson ? f.toJson() : f)));
                const updatedFeatureIds = this.getFeatureIds(updatedFeatures);

                // Update feature collections with successful edits
                const featureClassFeatures = this.getFeatures(featureClass);
                const currentFeatureIds = this.getFeatureIds(featureClassFeatures);

                featureClassFeatures.splice(
                    0,
                    featureClassFeatures.length,
                    ...featureClassFeatures.filter(
                        (f: any) =>
                            !updatedFeatureIds.includes(f.attributes.id) &&
                            !(f.attributes.objectid && deletesObjectIds.includes(f.attributes.objectid)) &&
                            !deletesIds.includes(f.attributes.id)
                    ),
                    ...updatedFeatures
                );

                // Track added and deleted IDs
                const addedDeletedIds = this.getAddedDeletedIds(featureClass);
                addedDeletedIds.splice(
                    0,
                    addedDeletedIds.length,
                    ...[
                        ...addedDeletedIds,
                        ...updatedFeatureIds.filter(v => !currentFeatureIds.includes(v)),
                        ...currentFeatureIds.filter(v => !updatedFeatureIds.includes(v))
                    ]
                        .sort((a, b) => a - b)
                        .filter((v, i, a) => a.indexOf(v) === i)
                );

                // Update other related feature classes (like points and lines)
                const pointFeatureClasses = ['point', 'controlpoint'];
                if (pointFeatureClasses.includes(featureClass)) {
                    pointFeatureClasses
                        .filter(fc => fc !== featureClass)
                        .forEach(fc => {
                            const fcFeatures = this.getFeatures(fc);
                            const fcFeaturesToKeep = fcFeatures.filter(
                                f =>
                                    !(f.attributes.objectid ? deletesObjectIds.includes(f.attributes.objectid) : deletesIds.includes(f.attributes.id))
                            );
                            if (fcFeaturesToKeep.length !== fcFeatures.length) {
                                fcFeatures.splice(0, fcFeatures.length, ...fcFeaturesToKeep);
                            }
                        });
                }

                const lineFeatureClasses = ['line', 'polyline'];
                if (lineFeatureClasses.includes(featureClass)) {
                    lineFeatureClasses
                        .filter(fc => fc !== featureClass)
                        .forEach(fc => {
                            const fcFeatures = this.getFeatures(fc);
                            const polylinesUpdated = featureClassFeatures.filter(
                                f => f.attributes.pointids && updatedFeatureIds.includes(f.attributes.id)
                            );
                            const polylineIdsUpdated = polylinesUpdated.map(f => f.attributes.id);
                            const fcFeaturesToKeep = fcFeatures.filter(
                                f =>
                                    !(f.attributes.objectid
                                        ? deletesObjectIds.includes(f.attributes.objectid)
                                        : deletesIds.includes(f.attributes.id)) && !polylineIdsUpdated.includes(f.attributes.id)
                            );

                            if (fcFeaturesToKeep.length !== fcFeatures.length || polylinesUpdated.length) {
                                fcFeatures.splice(0, fcFeatures.length, ...fcFeaturesToKeep, ...polylinesUpdated);
                            }
                        });
                }

                if (!options.client) {
                    this.featuresRepositoryStore.setPristineFeatures(featureClass, featureClassFeatures);
                }
                return {
                    adds: res.addResults,
                    updates: res.updateResults,
                    deletes: res.deleteResults
                };
            }),
            catchError(err => {
                console.error(`applyEdits() error: ${err}`);
                return throwError(err);
            })
        );
    }

    getEdits() {
        const featureClasses = this.getFeatureClasses();
        const pristineFeatureClassMap = this.featuresRepositoryStore.getPristineFeatureClassMap();
        const featureClassMap = this.featuresRepositoryStore.getFeatureClassMap();
        //ND-1142 preventive solution - checking and rewriting internalangle values of angles to DD:MM:SS format
        reCheckAngleFormats(featureClassMap['angle']);

        return featureClasses.reduce((acc, k) => {
            const pristineFeatures = pristineFeatureClassMap[k];
            const pristineFeatureIds = pristineFeatures.map(f => f.attributes.id);
            const pristineFeatureMap = pristineFeatures.reduce((accFeatures, f) => {
                accFeatures[f.attributes.id] = f;
                return accFeatures;
            }, {});

            const features = featureClassMap[k];
            const featureIds = features.map(f => f.attributes.id);
            const featureMap = features.reduce((accFeatures, f) => {
                const jsonFeature = f.toJson();
                this.featuresRepositoryStore.stripClientAttrs(k, jsonFeature);
                accFeatures[f.attributes.id] = jsonFeature;
                return accFeatures;
            }, {});

            const allFeatureIds = [...pristineFeatureIds, ...featureIds].filter((v, i, a) => i === a.indexOf(v)).sort((a, b) => a - b);
            const edits = allFeatureIds.reduce(
                (accEdits, featureId) => {
                    const pristineFeature = pristineFeatureMap[featureId];
                    const feature = featureMap[featureId];
                    if (pristineFeature && feature) {
                        feature.attributes.objectid = pristineFeature.attributes.objectid;
                        if (!equals(pristineFeature, feature)) {
                            accEdits.updates.push(feature);
                        }
                    } else if (pristineFeature) {
                        if (pristineFeature.attributes.objectid) {
                            //The feature has previously been persisted, so needs to be deleted server side
                            accEdits.deletes.push(pristineFeature);
                        }
                    } else {
                        accEdits.adds.push(feature);
                    }
                    return accEdits;
                },
                {adds: [], updates: [], deletes: []}
            );

            if (edits.adds.length || edits.updates.length || edits.deletes.length) {
                acc[k] = edits;
            }

            return acc;
        }, {});
    }

    hasEdits() {
        return this.hasEditsFlag;
    }

    interestHasUnsavedAssociations(interestId) {
        const interest = this.featuresRepositoryStore.getFeatureClassMap().interest.find(i => i.attributes.id === interestId);
        if (!interest) {
            return false;
        }

        const pristineInterest = this.featuresRepositoryStore.getPristineFeatureClassMap().interest.find(i => i.attributes.id === interestId);
        if (!pristineInterest) {
            return true;
        }

        return (
            interest.attributes.polylineids !== pristineInterest.attributes.polylineids ||
            interest.attributes.polygonids !== pristineInterest.attributes.polygonids
        );
    }

    save(): Observable<any> {
        const featureClassMap = this.featuresRepositoryStore.getFeatureClassMap();
        const allEdits = this.getEdits();

        const editObservables = Object.keys(allEdits).map(featureClassKey => {
            const edits = allEdits[featureClassKey];
            return this.applyEdits(featureClassKey, edits.adds, edits.updates, edits.deletes).pipe(
                tap(() => {
                    this.featuresRepositoryStore.setPristineFeatures(featureClassKey, featureClassMap[featureClassKey]);
                }),
                catchError(err => {
                    console.error(`Error applying edits for ${featureClassKey}:`, err);
                    return of();
                })
            );
        });

        // Provide at least one observable that emits a value to enable clients to use subscriptions on the returned observable.
        // Without this, if editObservables is empty, clients that subscribe will never have their subscriptions invoked, as no
        // values are emitted, which breaks the previous contract that was in place with promises
        editObservables.push(of(true));

        return forkJoinDefaultIfEmpty(editObservables).pipe(
            tap(() => {
                this.notificationAlertsService.showPlanSaveSuccess();
                this.hasEditsFlag = false;
            })
        );
    }
    /**
     * Initialise service
     * @return {Promise} that resolves when service is initialised
     */
    init() {
        console.info(`${this.title} - init()`);
        this.featureLayerMap = {};
        this.featuresRepositoryStore.clearAllFeatures();
        if (this.featureLayerMap) {
            delete this.featureLayerMap;
        }
        this.hasEditsFlag = false;
    }

    getFeatureLayerMap() {
        if (this.featureLayerMap) {
            return this.featureLayerMap;
        }

        const featureLayerConfig = this.appConfigService.getDesignProperties().DESIGN_SETTINGS.DEFAULT_MAP.LAYERS;
        const planDefinitionExpression = `guid = '${this.planModelService.getGuid()}'`;

        this.featureLayerMap = {
            angle: new this.esriModuleService.EsriFeatureLayer(
                featureLayerConfig.ANGLE.URL + this.arcGisAuthService.getTokenParam(),
                Object.assign({}, featureLayerConfig.ANGLE.OPTIONS, {
                    definitionExpression: planDefinitionExpression
                })
            ),
            annotation: new this.esriModuleService.EsriFeatureLayer(
                featureLayerConfig.ANNOTATION.URL + this.arcGisAuthService.getTokenParam(),
                Object.assign({}, featureLayerConfig.ANNOTATION.OPTIONS, {
                    definitionExpression: planDefinitionExpression
                })
            ),
            clipping: new this.esriModuleService.EsriFeatureLayer(
                featureLayerConfig.CLIPPING.URL + this.arcGisAuthService.getTokenParam(),
                Object.assign({}, featureLayerConfig.CLIPPING.OPTIONS, {
                    definitionExpression: planDefinitionExpression
                })
            ),
            constructpoint: new this.esriModuleService.EsriFeatureLayer(
                featureLayerConfig.CONSTRUCT_POINT.URL + this.arcGisAuthService.getTokenParam(),
                Object.assign({}, featureLayerConfig.CONSTRUCT_POINT.OPTIONS, {
                    definitionExpression: planDefinitionExpression
                })
            ),
            constructline: new this.esriModuleService.EsriFeatureLayer(
                featureLayerConfig.CONSTRUCT_LINE.URL + this.arcGisAuthService.getTokenParam(),
                Object.assign({}, featureLayerConfig.CONSTRUCT_LINE.OPTIONS, {
                    definitionExpression: planDefinitionExpression
                })
            ),
            enlargement: new this.esriModuleService.EsriFeatureLayer(
                featureLayerConfig.ENLARGEMENT.URL + this.arcGisAuthService.getTokenParam(),
                Object.assign({}, featureLayerConfig.ENLARGEMENT.OPTIONS, {
                    definitionExpression: planDefinitionExpression
                })
            ),
            interest: new this.esriModuleService.EsriFeatureLayer(
                featureLayerConfig.INTEREST.URL + this.arcGisAuthService.getTokenParam(),
                Object.assign({}, featureLayerConfig.INTEREST.OPTIONS, {
                    definitionExpression: planDefinitionExpression
                })
            ),
            line: new this.esriModuleService.EsriFeatureLayer(
                featureLayerConfig.LINE.URL + this.arcGisAuthService.getTokenParam(),
                Object.assign({}, featureLayerConfig.LINE.OPTIONS, {
                    definitionExpression: planDefinitionExpression
                })
            ),
            lot: new this.esriModuleService.EsriFeatureLayer(
                featureLayerConfig.LOT.URL + this.arcGisAuthService.getTokenParam(),
                Object.assign({}, featureLayerConfig.LOT.OPTIONS, {
                    definitionExpression: planDefinitionExpression
                })
            ),
            affectedlot: new this.esriModuleService.EsriFeatureLayer(
                featureLayerConfig.AFFECTED_LOT.URL + this.arcGisAuthService.getTokenParam(),
                Object.assign({}, featureLayerConfig.AFFECTED_LOT.OPTIONS, {
                    definitionExpression: planDefinitionExpression
                })
            ),
            controlpoint: new this.esriModuleService.EsriFeatureLayer(
                featureLayerConfig.POINT.URL + this.arcGisAuthService.getTokenParam(),
                Object.assign({}, featureLayerConfig.CONTROL_POINT.OPTIONS, {
                    definitionExpression: `${planDefinitionExpression} AND (type < 6 or type = 100)`
                })
            ),
            point: new this.esriModuleService.EsriFeatureLayer(
                featureLayerConfig.POINT.URL + this.arcGisAuthService.getTokenParam(),
                Object.assign({}, featureLayerConfig.POINT.OPTIONS, {
                    definitionExpression: `${planDefinitionExpression} AND (type >= 6 and type <> 100)`
                })
            ),
            polygon: new this.esriModuleService.EsriFeatureLayer(
                featureLayerConfig.POLYGON.URL + this.arcGisAuthService.getTokenParam(),
                Object.assign({}, featureLayerConfig.POLYGON.OPTIONS, {
                    definitionExpression: planDefinitionExpression
                })
            ),
            polyline: new this.esriModuleService.EsriFeatureLayer(
                featureLayerConfig.INTEREST_LINE.URL + this.arcGisAuthService.getTokenParam(),
                Object.assign({}, featureLayerConfig.INTEREST_LINE.OPTIONS, {
                    definitionExpression: `${planDefinitionExpression} AND pointids is not null`
                })
            ),
            road: new this.esriModuleService.EsriFeatureLayer(
                featureLayerConfig.ROAD.URL + this.arcGisAuthService.getTokenParam(),
                Object.assign({}, featureLayerConfig.ROAD.OPTIONS, {
                    definitionExpression: planDefinitionExpression
                })
            ),
            printarea: new this.esriModuleService.EsriFeatureLayer(
                featureLayerConfig.PRINT_AREA.URL + this.arcGisAuthService.getTokenParam(),
                Object.assign({}, featureLayerConfig.PRINT_AREA.OPTIONS, {
                    definitionExpression: planDefinitionExpression
                })
            )
        };

        return this.featureLayerMap;
    }

    getFeatureLayers() {
        const featureLayerMap = this.getFeatureLayerMap();
        return Object.keys(featureLayerMap).map(k => featureLayerMap[k]);
    }

    getFeatureLayer(featureClass: string) {
        return this.getFeatureLayerMap()[featureClass.toLowerCase()];
    }
}
